/**
 * Deep-link resume — gap J9 of `docs/specs/um-app/09-auth-onboarding.md` §4:
 *
 * > **Deep links** — an emailed pack or alert link lands on sign-in when signed out, then
 * > **resumes the intended destination** after redemption.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHAT WENT WRONG WITHOUT IT
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * A UM opens `…/marking/9901` from an alert on her phone. The device holds no credential, so
 * the session gate stops her, and the destination is gone: she signs in with her personal
 * link and arrives at the Command Desk, with no way back to the row she was sent to except
 * finding the original message again. At a venue, mid-tournament, that is a lost minute and
 * a lost thread.
 *
 * So the gate now carries the destination with it — `/sign-in?next=/marking/9901` — and the
 * redeem screen hands it back once the credential is stored.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THE RULE, WHICH IS THE WHOLE OF THE SECURITY OF THIS FILE
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * **A resume target is a path of THIS application, chosen from a list written here.** It is
 * never a URL. Everything below is one idea stated in several ways:
 *
 *   · It must begin with a single `/`. `https://evil.example` does not; nor does `evil`.
 *   · `//evil.example` and `/\evil.example` are refused explicitly. Both are read by browsers
 *     as protocol-relative *authorities* — `//host` is a URL, and every major browser folds
 *     a backslash into a forward slash before parsing — so a check that only looked for a
 *     scheme would wave both through and send the person off-origin.
 *   · It must MATCH A ROUTE THIS APPLICATION HAS. An allowlist, not a denylist: a new way to
 *     write an off-origin reference is a new denylist entry that nobody adds in time, while
 *     an allowlist is wrong only in the direction of refusing to resume — which costs a
 *     person one click, and costs nobody a redirect to somewhere hostile.
 *   · No fragment, ever. The fragment is where the sign-in token lives, and a destination is
 *     not a place to smuggle one back into an address bar.
 *   · `/sign-in` is not a destination. It is where we already are.
 *
 * A value that fails any of these is not repaired, and no error is shown: the person lands
 * on the Command Desk, exactly as they did before this file existed. A refused resume must
 * be indistinguishable from no resume — a screen that said *"that destination was rejected"*
 * would be a probe oracle for anybody crafting one.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHY `sessionStorage` AS WELL AS THE QUERY PARAMETER
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * The person does not sign in on the URL the gate redirected them to. They open the personal
 * link the Technical Delegate sent — a *different* URL, `/sign-in#token=…`, with no `next` on
 * it — and that navigation is what drops the query. So the destination is also remembered in
 * `sessionStorage`, which survives it, and is TAKEN (read and removed) on the way out, so a
 * destination is resumed once and never haunts the next sign-in on the same device.
 *
 * `sessionStorage` and not `localStorage`: it is scoped to the one tab, and it is gone when
 * the tab is. A destination naming an official is not a credential, but it is not something
 * to leave on a shared laptop either. The credential itself lives in IndexedDB and is not
 * this file's business (`10-data-model.md`).
 *
 * Storage is wrapped, because it throws rather than returning null in more browsers than
 * expected — Safari's private mode, an iframe with third-party storage blocked. Losing a
 * destination is a click; an exception on the sign-in screen is a white page at a venue.
 */

import { SIGN_IN_NEXT_PARAM, SIGN_IN_PATH } from "@hockeytd/shared";

/**
 * Where a sign-in lands with nothing to resume: the Command Desk, the first screen of the
 * Technical Delegate's day.
 *
 * It is the Control Centre's landing and NOT everybody's — see {@link landingPathFor}.
 */
export const LANDING_PATH = "/";

/** The Umpires Managers' own landing, which is the whole of their application. */
export const UM_LANDING_PATH = "/um";

/**
 * WHERE A KIND OF USER BELONGS when nothing else was asked for.
 *
 * Both kinds share one origin and one credential (stance D2), so for a while both landed on
 * `/`. That is the Control Centre: a masthead, a module strip and eleven panels whose every
 * read a UM credential is refused for. An Umpires Manager who signed in was therefore shown
 * the one surface in the system that is not hers, and had to be told to type `/um`.
 *
 * **This is routing and not authorisation, and the distinction is exact.** Nothing here
 * decides what anybody MAY do: `requireTd` and `requireUm` on the API decide that, and
 * `UmShell`'s own gate turns a TD away from the UM app in the client. A default destination
 * that suits the caller does not widen a single one of those — a UM who types a Control
 * Centre path still reaches it and is still refused by every read on it, exactly as before.
 *
 * The kind is known at every call site: it comes from the redeem response or from the live
 * session, both of which are `GET /auth/me`'s own answer about this device.
 */
export function landingPathFor(kind: "td" | "um"): string {
  return kind === "um" ? UM_LANDING_PATH : LANDING_PATH;
}

/** One key, one tab. Prefixed so it is obviously ours in a devtools storage pane. */
export const RESUME_STORAGE_KEY = "hockeytd.resume-path";

/**
 * The routes that may be resumed — `App.tsx`'s route table, minus two entries on purpose.
 *
 * **`/sign-in` is absent** because resuming to it is a loop, and **the `*` catch-all is
 * absent** because it is not a destination: it renders `StubPage` for anything unrecognised,
 * so admitting it would make the allowlist accept every path and mean nothing at all.
 *
 * `:param` stands for one path segment. A route gained here is a route gained in `App.tsx`
 * first; the pairing is asserted in `resume.test.tsx` against the module table, so a module
 * that becomes reachable and is not resumable is a failing test rather than a shrug.
 */
export const RESUMABLE_ROUTE_TEMPLATES: readonly string[] = [
  "/",
  "/marking",
  "/marking/:officialId",
  "/marking/:officialId/report",
  "/appointments/technical",
  "/appointments/umpires",
  "/fixtures",
  "/hearings",
  "/officials",
  "/officials/:officialId",
  "/configuration",
  // The UM app, on the same origin and behind the same gate (`UmShell`). These are the
  // routes J9 is actually written about — an emailed pack link and a match alert both land
  // on a phone, which is the device least likely to be signed in already.
  "/um",
  "/um/match/:tmsMatchDbId",
];

/**
 * One path segment: TMS person ids (`9901`), uuids and the display ids in between. No `%`,
 * so a percent-escape cannot be smuggled through a segment and decoded into a `/` or a `.`
 * by something downstream; no `.` runs, so `..` cannot climb.
 */
const SEGMENT = "[A-Za-z0-9][A-Za-z0-9_-]{0,63}";

const RESUMABLE_ROUTES: readonly RegExp[] = RESUMABLE_ROUTE_TEMPLATES.map((template) => {
  const pattern = template
    .split("/")
    .map((part) => (part.startsWith(":") ? SEGMENT : part.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")))
    .join("/");
  return new RegExp(`^${pattern}$`);
});

/** The characters a query string may be built from — RFC 3986's `query` set, and nothing else. */
const SAFE_QUERY = /^[A-Za-z0-9._~!$&'()*+,;=:@/?%-]*$/;

/**
 * The one gate. Returns the path to resume to, or `null` — which every caller reads as
 * "the Command Desk", never as an error.
 *
 * The value arrives already percent-decoded, because it comes out of `URLSearchParams.get`
 * or out of storage; `https%3A%2F%2Fevil.example` is therefore `https://evil.example` by the
 * time it is seen here, and is refused for not beginning with `/` like any other URL.
 */
export function sanitiseResumePath(value: string | null | undefined): string | null {
  if (typeof value !== "string") return null;

  const candidate = value.trim();
  if (candidate === "") return null;
  // Control characters, which some parsers strip before resolving a URL — `/\tevil` and
  // friends. Refused whole rather than stripped, because stripping is how a bypass is built.
  if (/[\u0000-\u001F\u007F]/.test(candidate)) return null;
  if (!candidate.startsWith("/")) return null;
  if (candidate.startsWith("//")) return null;
  if (candidate.includes("\\")) return null;
  if (candidate.includes("#")) return null;

  const [path, query, ...extra] = candidate.split("?");
  if (path === undefined || extra.length > 0) return null;
  if (path === SIGN_IN_PATH) return null;
  if (!RESUMABLE_ROUTES.some((route) => route.test(path))) return null;
  if (query !== undefined && !SAFE_QUERY.test(query)) return null;

  return query === undefined || query === "" ? path : `${path}?${query}`;
}

/**
 * `/sign-in`, carrying `next` when there is a destination worth carrying.
 *
 * The Command Desk is not one: it is where a sign-in lands anyway, so `next=%2F` would be
 * noise in the address bar of the ordinary case, which is most of them.
 */
export function signInPathWithNext(intended: string | null | undefined): string {
  const path = sanitiseResumePath(intended);
  if (path === null || path === LANDING_PATH) return SIGN_IN_PATH;
  return `${SIGN_IN_PATH}?${SIGN_IN_NEXT_PARAM}=${encodeURIComponent(path)}`;
}

/**
 * Remember where the person was going, for the sign-in that has not happened yet.
 *
 * `null`/`undefined` means "nothing was offered" and leaves any earlier destination standing
 * — the personal link arrives with no `next` on it, and it must not wipe the one the gate
 * recorded a moment earlier. A value that IS offered and is refused clears the store, so a
 * crafted destination cannot leave a stale real one to be resumed in its place.
 */
export function rememberResumePath(value: string | null | undefined): void {
  if (value === null || value === undefined) return;
  const path = sanitiseResumePath(value);
  try {
    if (path === null) window.sessionStorage.removeItem(RESUME_STORAGE_KEY);
    else window.sessionStorage.setItem(RESUME_STORAGE_KEY, path);
  } catch {
    // Storage refused. The destination is lost and the person lands on the Command Desk.
  }
}

/**
 * The remembered destination, removed as it is read — resumed once, never twice.
 *
 * It is sanitised again on the way out. The store is this tab's own, so that is belt and
 * braces; it costs one regular expression and it means the allowlist is enforced at the
 * point of USE, which is where an allowlist has to hold.
 */
export function takeResumePath(): string | null {
  let stored: string | null = null;
  try {
    stored = window.sessionStorage.getItem(RESUME_STORAGE_KEY);
    window.sessionStorage.removeItem(RESUME_STORAGE_KEY);
  } catch {
    return null;
  }
  return sanitiseResumePath(stored);
}

/**
 * Where to go now that the session is real: the destination if there is one, and otherwise
 * the landing screen of this kind of user.
 *
 * The remembered destination wins over the kind, and that is right: it is where the person
 * was actually going when they were stopped, and it was already put through the allowlist
 * above. It is not filtered by kind here — the allowlist is about where this application will
 * send a browser, and who may READ what it finds there is the API's answer to give, not a
 * redirect's. A UM resumed onto a Control Centre path lands on a page that refuses her, which
 * is the same thing that happens if she types it.
 */
export function resumeTarget(kind: "td" | "um"): string {
  return takeResumePath() ?? landingPathFor(kind);
}
